import {
  MfaCodeKind,
  normalizeRecoveryCodeInput,
  normalizeTotpCode,
  RECOVERY_CODE_INPUT_LENGTH,
  TOTP_CODE_LENGTH,
} from '@/utils/mfa';
import { TextField, TextFieldProps } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React from 'react';

const useStyles = makeStyles((theme) => ({
  input: {
    ...theme.typography.h6,
    fontFamily: 'monospace',
  },
  totp: {
    letterSpacing: '0.3em',
  },
  recovery: {
    letterSpacing: '0.08em',
  },
}));

type MfaCodeFieldProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  /** 'totp' takes 6 digits; 'recovery' takes a code like 7KQ2M-9XD4R. */
  kind: MfaCodeKind;
  value: string;
  onChange: (value: string) => void;
};

export default function MfaCodeField({
  kind,
  value,
  onChange,
  inputProps,
  ...props
}: MfaCodeFieldProps): JSX.Element {
  const classes = useStyles();
  const totp = kind === 'totp';

  return (
    <TextField
      variant="outlined"
      fullWidth
      id="mfa-code"
      name="code"
      label={totp ? '6-digit code' : 'Recovery code'}
      placeholder={totp ? undefined : 'XXXXX-XXXXX'}
      autoComplete={totp ? 'one-time-code' : 'off'}
      value={value}
      onChange={(event) =>
        onChange(
          totp
            ? normalizeTotpCode(event.target.value)
            : normalizeRecoveryCodeInput(event.target.value),
        )
      }
      inputProps={{
        className: clsx(classes.input, totp ? classes.totp : classes.recovery),
        inputMode: totp ? 'numeric' : 'text',
        pattern: totp ? '[0-9]*' : undefined,
        maxLength: totp ? TOTP_CODE_LENGTH : RECOVERY_CODE_INPUT_LENGTH,
        autoCapitalize: 'characters',
        spellCheck: false,
        ...inputProps,
      }}
      {...props}
    />
  );
}
